/**
 * BI > Kardex — paridade com portal cliente (kardex.php).
 * API: GET .../reports/kardex e .../reports/kardex/summary
 */
import api from '../api.js';
import { translate } from '../../versa/filter.js';
import msgbox, { notify } from '../../versa/msgbox.js';
import {
	activeCols,
	buildKardexApiParams,
	cellValueForCol,
	exportCellPlain,
	enrichKardexRows,
	fetchLocationLabelMap,
	filterRowsForClient,
	filterEntradaStagingNoise,
	filterStagingOnlyMoves,
	fmtDateOnly,
	fmtNumberPt,
	summaryDisplayItems,
	summaryForClientView,
	summaryAlignClosingWithStock,
	kardexCellClass,
	kardexNumericCell,
	stockTotalsForProduct,
} from '../utils/kardex-portal-parity.js';
import { formatBiHttpError } from './bi-http-error.js';

const KDX_PAGE_LIMIT = 300;
const KDX_FETCH_CHUNK = 200;

function $(sel, root = document) {
	return root.querySelector(sel);
}

function esc(s) {
	if (s == null) return '';
	const t = document.createElement('template');
	t.textContent = String(s);
	return t.innerHTML;
}

function buildQuery(params) {
	const sp = new URLSearchParams();
	for (const [k, v] of Object.entries(params)) {
		if (v === undefined || v === null || v === '') continue;
		sp.set(k, String(v));
	}
	return sp.toString();
}

function skuLabel(p) {
	if (!p?.sku) return '';
	return Array.isArray(p.sku) ? p.sku.join('') : String(p.sku);
}

async function loadCompanies(selectEl) {
	const res = await api.list_all_companies();
	if (!res.ok) {
		notify(await res.text(), 'Empresas');
		return;
	}
	const list = await res.json();
	if (!Array.isArray(list)) {
		notify('Lista de empresas em formato inesperado.', 'Empresas');
		return;
	}
	selectEl.innerHTML = '<option value="">— Selecione —</option>';
	for (const c of list) {
		const o = document.createElement('option');
		o.value = c.id;
		o.textContent = c.nick || c.name || c.id;
		selectEl.appendChild(o);
	}
}

/**
 * Busca produtos na API SIAL (operadores válidos: co/bw/eq — não existe sw).
 * Paridade com portal: termo no SKU, nome ou id.
 */
async function searchProducts(companyId, term) {
	const needle = String(term || '').trim();
	if (!companyId || !needle) return [];

	const base = {
		'field-0': 'company_id', 'operator-0': 'eq', 'value-0': companyId,
		'connector-1': 'e',
	};

	const filters = [
		translate({ ...base, 'field-1': 'name', 'operator-1': 'co', 'value-1': needle }),
		translate({ ...base, 'field-1': 'sku', 'operator-1': 'co', 'value-1': needle }),
	];
	if (/^\d+$/.test(needle)) {
		filters.unshift(translate({ ...base, 'field-1': 'sku', 'operator-1': 'eq', 'value-1': needle }));
		// id interno costuma ser curto; SKU numérico longo não deve filtrar por id
		if (needle.length <= 7) {
			filters.unshift(translate({ ...base, 'field-1': 'id', 'operator-1': 'eq', 'value-1': needle }));
		}
	}

	const seen = new Set();
	const out = [];
	for (const fe of filters) {
		const q = `?filter=${encodeURIComponent(fe)}&limit=20&include_disabled=1`;
		try {
			const rows = await api.call(api.list_products, q);
			if (!Array.isArray(rows)) continue;
			for (const p of rows) {
				if (p?.id == null || seen.has(p.id)) continue;
				seen.add(p.id);
				out.push(p);
			}
			if (out.length >= 20) break;
		} catch (e) {
			console.warn('Kardex busca produto', e);
		}
	}
	return out;
}

export default async function bi_kardex() {
	const work = document.getElementById('workarea');
	work.innerHTML = `
		<style>
			.kdx-wrap { max-width: 96rem; margin: 0 auto; }
			.kdx-header { background: #1e3a5f; color: #fff; padding: 14px 18px; border-radius: 0 0 10px 10px; margin-bottom: 1rem; }
			.kdx-header h1 { font-size: 1.15rem; margin: 0; }
			.kdx-header .sub { font-size: 0.8rem; opacity: 0.92; margin-top: 4px; }
			.kdx-card { background: #fff; border: 1px solid #e2e8f0; border-radius: 10px; padding: 14px; margin-bottom: 1rem; box-shadow: 0 1px 3px rgba(0,0,0,0.06); }
			.kdx-filter-title { font-size: 0.7rem; font-weight: 700; color: #94a3b8; text-transform: uppercase; margin-bottom: 8px; }
			.kdx-filters { display: flex; flex-wrap: wrap; gap: 0.75rem; align-items: flex-end; }
			.kdx-filters label { display: flex; flex-direction: column; font-size: 0.85rem; gap: 2px; }
			.kdx-filters input, .kdx-filters select { padding: 4px 8px; font-size: 0.85rem; min-height: 2rem; }
			.kdx-prod-pick { max-height: 220px; overflow-y: auto; border: 1px solid #e2e8f0; border-radius: 8px; margin-top: 4px; display: none; }
			.kdx-prod-pick button { display: block; width: 100%; text-align: left; border: 0; border-bottom: 1px solid #f1f5f9; padding: 8px 12px; background: #fff; cursor: pointer; font-size: 0.85rem; }
			.kdx-prod-pick button:hover { background: #f0f9ff; }
			.kdx-qty-in { color: #047857; font-weight: 700; }
			.kdx-qty-out { color: #c2410c; font-weight: 700; }
			#workarea table.kdx-table td.kdx-qty-in,
			#workarea table.kdx-table td.kdx-qty-out {
				font-size: inherit;
				font-variant-numeric: tabular-nums;
			}
			#workarea table.kdx-table td.kdx-kardex-nf { color: #0369a1; font-weight: 600; }
			#workarea table.kdx-table td.kdx-kardex-his { color: #b45309; font-weight: 600; }
			.kdx-legenda { font-size: 0.78rem; color: #475569; background: #f1f5f9; border-radius: 8px; padding: 10px 12px; border: 1px solid #e2e8f0; margin-top: 10px; line-height: 1.45; }
			.kdx-alert { background: #fef2f2; color: #991b1b; border: 1px solid #fecaca; padding: 8px 12px; border-radius: 8px; margin-bottom: 1rem; display: none; }
			.kdx-actions { display: flex; gap: 0.5rem; flex-wrap: wrap; align-items: center; margin-top: 0.75rem; }
			.kdx-meta { font-size: 0.8rem; color: #64748b; padding: 10px 14px; border-bottom: 1px solid #e2e8f0; display: flex; justify-content: space-between; flex-wrap: wrap; gap: 8px; background: #fafafa; }
		</style>
		<div class="kdx-wrap">
			<div class="kdx-header">
				<h1>Kardex / Movimentações</h1>
				<div class="sub">Extrato no período · saldo na grade é acumulado no período (não é estoque físico de hoje — ver rodapé «Estoque atual»).</div>
			</div>
			<div class="kdx-card">
				<div class="kdx-filter-title">Filtros</div>
				<div class="kdx-filters">
					<label style="min-width:14rem">Empresa<sup>*</sup><select id="kdx-company"></select></label>
					<label style="min-width:16rem">Código item / descrição
						<div style="display:flex;gap:4px">
							<input type="search" id="kdx-busca-prod" placeholder="Digite e busque…" autocomplete="off">
							<button type="button" class="button" id="kdx-btn-buscar-prod">Buscar</button>
						</div>
					</label>
					<label>Data inicial<sup>*</sup><input type="date" id="kdx-df"></label>
					<label>Data final (inclui)<sup>*</sup><input type="date" id="kdx-dt"></label>
					<label>Lote (opcional)<input type="text" id="kdx-lot" placeholder="Filtrar por lote"></label>
					<label>Valores
						<select id="kdx-cost">
							<option value="none" selected>Sem valores monetários</option>
							<option value="transaction">Valores por movimento</option>
						</select>
					</label>
					<label style="align-self:center"><input type="checkbox" id="kdx-dis" checked> Incluir movimentos desabilitados</label>
					<label style="align-self:center"><input type="checkbox" id="kdx-vista-cliente"> Vista cliente (extrato simplificado)</label>
					<button type="button" class="button" id="kdx-consultar">Consultar</button>
				</div>
				<div id="kdx-prod-pick" class="kdx-prod-pick"></div>
				<div id="kdx-prod-sel" class="mts" style="color:#0369a1;font-weight:600"></div>
				<input type="hidden" id="kdx-product-id" value="">
				<div class="kdx-legenda" id="kdx-legenda"></div>
			</div>
			<div id="kdx-alert" class="kdx-alert"></div>
			<div id="kdx-summary" class="kdx-summary" style="display:none"></div>
			<div class="kdx-card" style="padding:0">
				<div class="kdx-meta">
					<span>Gerado em <strong id="kdx-gerado">—</strong> · <span id="kdx-rows-meta"></span></span>
					<span id="kdx-pager" style="display:none">
						<button type="button" class="button" id="kdx-prev" disabled>Anterior</button>
						<button type="button" class="button" id="kdx-next" disabled>Próxima</button>
					</span>
				</div>
				<div class="kdx-table-scroll">
					<table class="kardex-table kdx-table" id="kdx-table">
						<thead><tr><th colspan="20" style="color:#64748b;background:#f8fafc;font-weight:500">Use «Consultar» para carregar o extrato</th></tr></thead>
						<tbody></tbody>
					</table>
				</div>
			</div>
			<div id="kdx-stock-footer" class="kdx-card" style="display:none">
				<div class="kdx-filter-title">Estoque atual do produto <span style="font-weight:normal;color:#64748b">(livre + bloqueado)</span></div>
				<p class="mts" style="font-size:0.8rem;color:#64748b">Fonte: API <code>/stocks</code> — deve coincidir com «Saldo ao fim do período» e última linha do extrato.</p>
				<div class="kdx-summary" id="kdx-stock-kpis"></div>
			</div>
			<div class="kdx-actions">
				<button type="button" class="button" id="kdx-pdf" disabled>PDF</button>
				<button type="button" class="button" id="kdx-csv" disabled>CSV</button>
			</div>
		</div>
	`;

	const state = {
		companyId: '',
		companyName: '',
		productId: '',
		productLabel: '',
		offset: 0,
		total: null,
		lastSummary: null,
		lastStock: null,
		labelMap: {},
		locations: [],
		stagingIds: {},
		awaitingIds: {},
	};

	function defaultDates() {
		const now = new Date();
		const y = now.getFullYear();
		const m = String(now.getMonth() + 1).padStart(2, '0');
		const d = String(now.getDate()).padStart(2, '0');
		$('#kdx-dt', work).value = `${y}-${m}-${d}`;
		$('#kdx-df', work).value = `${y}-${m}-01`;
	}

	function syncLegenda() {
		const vista = $('#kdx-vista-cliente', work).checked;
		const pager = $('#kdx-pager', work);
		if (pager) pager.style.display = vista ? 'none' : '';
		const leg = $('#kdx-legenda', work);
		if (!leg) return;
		leg.innerHTML = vista
			? '<strong>Vista cliente</strong> — extrato simplificado com NF na entrada e ref. cliente nas saídas. Oculta transferências internas, reserva na posição e estornos. Saldo recalculado no período completo.'
			: '<strong>Vista completa</strong> — NF entrada, ref. cliente/HIS, bloqueio, lote atendido. Oculta endereçamento cancelado/estorno de entrada e movimentos só entre staging genérico. Paginação de até 300 linhas.'
				+ '<div style="margin-top:8px"><strong>Fluxo (origem→destino):</strong> '
				+ '<strong>I</strong> Entrada · <strong>L</strong> Lote · <strong>R</strong> Ressuprimento · '
				+ '<strong>O</strong> Saída · <strong>B</strong> Ajuste. Ex.: L → R = lote para picking.</div>';
	}

	function showErr(msg) {
		const el = $('#kdx-alert', work);
		el.textContent = msg;
		el.style.display = msg ? '' : 'none';
		if (msg) el.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
	}

	function findExactSkuMatch(products, term) {
		const n = String(term || '').trim();
		if (!n) return null;
		return products.find((p) => {
			if (!p?.sku) return false;
			const skus = Array.isArray(p.sku) ? p.sku : [p.sku];
			return skus.some((s) => String(s) === n);
		}) || null;
	}

	function applyProductSelection(p, { hidePick = true } = {}) {
		const sku = skuLabel(p);
		$('#kdx-product-id', work).value = String(p.id);
		state.productId = String(p.id);
		state.productLabel = `${sku} — ${p.name || ''}`;
		$('#kdx-prod-sel', work).textContent = `Selecionado: ${state.productLabel}`;
		if (hidePick) $('#kdx-prod-pick', work).style.display = 'none';
	}

	function renderPickList(products) {
		const box = $('#kdx-prod-pick', work);
		box.replaceChildren();
		if (!products.length) {
			box.innerHTML = '<div style="padding:8px;color:#64748b;font-size:0.85rem">Nenhum produto encontrado.</div>';
			box.style.display = '';
			return;
		}
		const hint = document.createElement('div');
		hint.style.cssText = 'padding:6px 12px;font-size:0.78rem;color:#64748b;background:#f8fafc;border-bottom:1px solid #e2e8f0';
		hint.textContent = products.length === 1
			? 'Um produto encontrado — clique para confirmar ou use «Consultar».'
			: `${products.length} produtos — clique no item correto.`;
		box.appendChild(hint);
		for (const p of products) {
			const b = document.createElement('button');
			const sku = skuLabel(p);
			b.innerHTML = `<strong>${esc(sku)}</strong> · ${esc(p.name || '')}`;
			b.addEventListener('click', () => applyProductSelection(p));
			box.appendChild(b);
		}
		box.style.display = '';
	}

	async function resolveProductSelection() {
		let pid = $('#kdx-product-id', work).value.trim();
		if (pid) return pid;
		const cid = $('#kdx-company', work).value;
		const term = $('#kdx-busca-prod', work).value.trim();
		if (!cid || !term) return '';
		const products = await searchProducts(cid, term);
		const exact = findExactSkuMatch(products, term);
		const pick = exact || (products.length === 1 ? products[0] : null);
		if (pick) {
			applyProductSelection(pick);
			return String(pick.id);
		}
		if (products.length > 1) {
			renderPickList(products);
			showErr('Vários produtos encontrados — escolha um na lista abaixo.');
			return '';
		}
		showErr('Produto não encontrado. Ajuste o código ou descrição e busque novamente.');
		return '';
	}

	async function handleBuscarProd() {
		const cid = $('#kdx-company', work).value;
		const term = $('#kdx-busca-prod', work).value.trim();
		const box = $('#kdx-prod-pick', work);
		if (!cid || !term) {
			box.style.display = 'none';
			if (!cid) showErr('Selecione a empresa.');
			return false;
		}
		showErr('');
		const products = await searchProducts(cid, term);
		const exact = findExactSkuMatch(products, term);
		if (exact || products.length === 1) {
			applyProductSelection(exact || products[0]);
			return true;
		}
		renderPickList(products);
		return false;
	}

	function commonParams() {
		return {
			productId: state.productId,
			dateFrom: $('#kdx-df', work).value,
			dateFim: $('#kdx-dt', work).value,
			lotText: $('#kdx-lot', work).value,
			costMode: $('#kdx-cost', work).value,
			includeDisabled: $('#kdx-dis', work).checked,
		};
	}

	async function ensureLocationMap() {
		if (!state.companyId) return;
		const { labels, locations, stagingIds, awaitingIds } = await fetchLocationLabelMap(
			state.companyId,
			null,
		);
		state.labelMap = labels;
		state.locations = locations;
		state.stagingIds = stagingIds;
		state.awaitingIds = awaitingIds;
	}

	async function fetchKardexPage(offset, limit) {
		const q = buildQuery(buildKardexApiParams({ ...commonParams(), limit, offset }));
		const res = await api.kardex_report(state.companyId, q);
		const raw = await res.text();
		if (!res.ok) throw new Error(formatBiHttpError(res.status, raw));
		return JSON.parse(raw);
	}

	async function fetchAllRows(onProgress) {
		const out = [];
		let off = 0;
		let batch = 0;
		while (out.length < 50000) {
			batch += 1;
			if (onProgress) onProgress(batch, off, out.length);
			const payload = await fetchKardexPage(off, KDX_FETCH_CHUNK);
			const rows = payload.rows || [];
			out.push(...rows);
			if (rows.length < KDX_FETCH_CHUNK) break;
			off += KDX_FETCH_CHUNK;
		}
		return out;
	}

	async function processRows(rawRows, { vistaCliente }) {
		let rows = filterStagingOnlyMoves(rawRows, state.stagingIds, state.awaitingIds);
		rows = filterEntradaStagingNoise(rows);
		await enrichKardexRows(rows, state.companyId, state.productId, {
			listInputs: null,
			listOutputs: null,
			listOutputOrders: null,
		});
		if (vistaCliente) {
			rows = filterRowsForClient(rows);
		}
		return rows;
	}

	function renderSummary(summary) {
		const wrap = $('#kdx-summary', work);
		const items = summaryDisplayItems(summary, {
			companyName: state.companyName,
			productLabel: state.productLabel,
			dateFimInclusive: $('#kdx-dt', work).value,
		});
		if (!items.length) {
			wrap.style.display = 'none';
			return;
		}
		wrap.replaceChildren();
		for (const { label, value } of items) {
			const div = document.createElement('div');
			div.className = 'kdx-kpi';
			const lbl = document.createElement('div');
			lbl.className = 'kpi-lbl';
			lbl.textContent = label;
			const val = document.createElement('div');
			val.className = 'kpi-val';
			val.textContent = value;
			div.appendChild(lbl);
			div.appendChild(val);
			wrap.appendChild(div);
		}
		const hint = document.createElement('div');
		hint.className = 'kdx-summary-hint';
		hint.textContent = 'Entradas: I→L concluídas. Saídas: efeito físico no período. Endereçamento cancelado/estorno de entrada (I→L desabilitada, L→I) não aparece no extrato. Saldo ao fim = SUM(/stocks).';
		wrap.appendChild(hint);
		wrap.style.display = '';
	}

	function renderStockFooter(totals) {
		const box = $('#kdx-stock-footer', work);
		const kpis = $('#kdx-stock-kpis', work);
		if (!totals) {
			box.style.display = 'none';
			return;
		}
		kpis.replaceChildren();
		for (const [lbl, key] of [['Total livre', 'qty_livre'], ['Total bloqueado', 'qty_bloqueado'], ['Total geral', 'qty_total']]) {
			const div = document.createElement('div');
			div.className = 'kdx-kpi';
			const l = document.createElement('div');
			l.className = 'kpi-lbl';
			l.textContent = lbl;
			const v = document.createElement('div');
			v.className = 'kpi-val';
			v.textContent = fmtNumberPt(totals[key]);
			div.appendChild(l);
			div.appendChild(v);
			kpis.appendChild(div);
		}
		box.style.display = '';
	}

	function kardexClientHighlightClass(colKey) {
		if (colKey === '__nf_entrada' || colKey === '__nf_entrada_html') return 'kdx-kardex-nf';
		if (colKey === '__req_his' || colKey === '__req_his_html') return 'kdx-kardex-his';
		return '';
	}

	function renderTableLoading(vistaCliente) {
		const costMode = $('#kdx-cost', work).value;
		const spec = activeCols(vistaCliente, costMode);
		const thead = $('#kdx-table thead', work);
		const tbody = $('#kdx-table tbody', work);
		thead.replaceChildren();
		tbody.replaceChildren();
		const headTr = document.createElement('tr');
		for (const c of spec) {
			const th = document.createElement('th');
			th.textContent = c.label;
			if (c.align === 'end') th.classList.add('kdx-end');
			else if (c.align === 'center') th.classList.add('kdx-center');
			headTr.appendChild(th);
		}
		thead.appendChild(headTr);
		const tr = document.createElement('tr');
		const td = document.createElement('td');
		td.colSpan = Math.max(spec.length, 1);
		td.textContent = 'Carregando movimentações…';
		td.style.cssText = 'color:#64748b;background:#f8fafc;font-weight:500;text-align:center;padding:1rem';
		tr.appendChild(td);
		tbody.appendChild(tr);
	}

	function renderTable(rows, vistaCliente) {
		const costMode = $('#kdx-cost', work).value;
		const spec = activeCols(vistaCliente, costMode);
		const thead = $('#kdx-table thead', work);
		const tbody = $('#kdx-table tbody', work);
		tbody.replaceChildren();
		if (!rows.length) {
			thead.replaceChildren();
			const tr = document.createElement('tr');
			const th = document.createElement('th');
			th.colSpan = Math.max(spec.length, 8);
			th.textContent = 'Nenhuma movimentação no período';
			th.style.cssText = 'color:#64748b;background:#f8fafc;font-weight:500';
			tr.appendChild(th);
			thead.appendChild(tr);
			return;
		}
		thead.replaceChildren();
		const headTr = document.createElement('tr');
		for (const c of spec) {
			const th = document.createElement('th');
			th.textContent = c.label;
			if (c.align === 'end') th.classList.add('kdx-end');
			else if (c.align === 'center') th.classList.add('kdx-center');
			headTr.appendChild(th);
		}
		thead.appendChild(headTr);
		const baseNum = vistaCliente ? 0 : state.offset;
		rows.forEach((r, idx) => {
			const tr = document.createElement('tr');
			const n = baseNum + idx + 1;
			for (const c of spec) {
				const td = document.createElement('td');
				const alignCls = c.align === 'end' ? 'kdx-end' : c.align === 'center' ? 'kdx-center' : '';
				const refCls = kardexCellClass(c.key);
				const hiCls = kardexClientHighlightClass(c.key);
				td.className = [alignCls, refCls, hiCls].filter(Boolean).join(' ');
				const numericCell = kardexNumericCell(r, c.key);
				if (numericCell) {
					td.textContent = numericCell.text;
					if (numericCell.tone === 'out') td.classList.add('kdx-qty-out');
					else if (numericCell.tone === 'in') td.classList.add('kdx-qty-in');
				} else {
					td.textContent = cellValueForCol(r, c.key, n, state.labelMap, { html: false });
				}
				tr.appendChild(td);
			}
			tbody.appendChild(tr);
		});
	}

	async function loadStockTotals() {
		state.lastStock = await stockTotalsForProduct(state.companyId, state.productId, {
			listStocks: (q) => api.call(api.list_stocks, q),
			listLots: (q) => api.call(api.list_lots, q),
		});
		renderStockFooter(state.lastStock);
	}

	async function runConsult() {
		showErr('');
		state.companyId = $('#kdx-company', work).value;
		state.productId = await resolveProductSelection();
		const coOpt = $('#kdx-company', work).selectedOptions?.[0];
		state.companyName = coOpt?.textContent || '';
		if (!state.companyId) {
			showErr('Selecione a empresa.');
			return;
		}
		if (!state.productId) return;
		const df = $('#kdx-df', work).value;
		const dt = $('#kdx-dt', work).value;
		if (!df || !dt) {
			showErr('Informe data inicial e final.');
			return;
		}

		const btn = $('#kdx-consultar', work);
		btn.disabled = true;
		$('#kdx-pdf', work).disabled = true;
		$('#kdx-csv', work).disabled = true;
		const vista = $('#kdx-vista-cliente', work).checked;
		renderTableLoading(vista);
		const mb = msgbox({ animation: true, text: 'Carregando Kardex…' });

		try {
			await ensureLocationMap();
			const sumQ = buildQuery(buildKardexApiParams(commonParams()));
			const sumRes = await api.kardex_summary(state.companyId, sumQ);
			const sumRaw = await sumRes.text();
			if (!sumRes.ok) throw new Error(formatBiHttpError(sumRes.status, sumRaw));
			state.lastSummary = JSON.parse(sumRaw);

			state.offset = 0;
			let rows;
			if (vista) {
				const raw = await fetchAllRows();
				rows = await processRows(raw, { vistaCliente: true });
				state.total = rows.length;
				const dropped = raw.length - rows.length;
				$('#kdx-rows-meta', work).textContent = `Vista cliente: ${fmtNumberPt(rows.length)} movimentação(ões)${dropped > 0 ? ` (${dropped} interna(s) oculta(s))` : ''} · período completo`;
			} else {
				const payload = await fetchKardexPage(state.offset, KDX_PAGE_LIMIT);
				const raw = payload.rows || [];
				state.total = payload.total_rows_in_period ?? raw.length;
				rows = await processRows(raw, { vistaCliente: false });
				$('#kdx-rows-meta', work).textContent = `Total: ${fmtNumberPt(state.total)} · página a partir de ${state.offset + 1}`;
				$('#kdx-prev', work).disabled = state.offset <= 0;
				$('#kdx-next', work).disabled = raw.length < KDX_PAGE_LIMIT
					|| (state.total != null && state.offset + raw.length >= state.total);
			}
			$('#kdx-gerado', work).textContent = new Date().toLocaleString('pt-BR', { hour12: false });
			await loadStockTotals();
			renderTable(rows, vista);
			const summaryView = vista
				? summaryForClientView(state.lastSummary, rows)
				: state.lastSummary;
			renderSummary(summaryAlignClosingWithStock(summaryView, state.lastStock));
			$('#kdx-pdf', work).disabled = false;
			$('#kdx-csv', work).disabled = false;
		} catch (e) {
			console.error(e);
			showErr(e?.message || 'Falha ao consultar o Kardex.');
			renderTable([], false);
		} finally {
			mb.destroy();
			btn.disabled = false;
		}
	}

	async function buildExportRows() {
		await ensureLocationMap();
		const vista = $('#kdx-vista-cliente', work).checked;
		const raw = await fetchAllRows();
		return processRows(raw, { vistaCliente: vista });
	}

	function downloadCsv(rows) {
		const spec = activeCols($('#kdx-vista-cliente', work).checked, $('#kdx-cost', work).value);
		let csv = '\uFEFF';
		csv += `${spec.map((c) => `"${c.label.replace(/"/g, '""')}"`).join(';')}\n`;
		rows.forEach((r, idx) => {
			csv += `${spec.map((c) => `"${String(exportCellPlain(r, c, idx + 1, state.labelMap)).replace(/"/g, '""')}"`).join(';')}\n`;
		});
		if (state.lastStock) {
			csv += '\n';
			csv += `"Estoque atual — total livre";"${fmtNumberPt(state.lastStock.qty_livre)}"\n`;
			csv += `"Estoque atual — total bloqueado";"${fmtNumberPt(state.lastStock.qty_bloqueado)}"\n`;
			csv += `"Estoque atual — total geral";"${fmtNumberPt(state.lastStock.qty_total)}"\n`;
		}
		const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
		const a = document.createElement('a');
		a.href = URL.createObjectURL(blob);
		a.download = `kardex_${new Date().toISOString().slice(0, 10)}.csv`;
		a.click();
		URL.revokeObjectURL(a.href);
	}

	function downloadPdf(rows) {
		const spec = activeCols($('#kdx-vista-cliente', work).checked, $('#kdx-cost', work).value);
		const head = spec.map((c) => `<th>${esc(c.label)}</th>`).join('');
		let body = '';
		rows.forEach((r, idx) => {
			body += '<tr>';
			for (const c of spec) {
				const v = exportCellPlain(r, c, idx + 1, state.labelMap);
				const cls = c.align === 'end' ? ' class="num"' : '';
				body += `<td${cls}>${esc(v)}</td>`;
			}
			body += '</tr>';
		});
		const stock = state.lastStock
			? `<p>Estoque atual: ${fmtNumberPt(state.lastStock.qty_livre)} / ${fmtNumberPt(state.lastStock.qty_bloqueado)} / ${fmtNumberPt(state.lastStock.qty_total)}</p>`
			: '';
		const html = `<!DOCTYPE html><html lang="pt-BR"><head><meta charset="UTF-8"><title>Kardex</title>
			<style>body{font-family:Segoe UI,sans-serif;font-size:11px;padding:16px}table{border-collapse:collapse;width:100%}
			th,td{border:1px solid #333;padding:4px 6px}.num{text-align:right}</style></head><body>
			<h1>Kardex</h1><p><strong>Empresa:</strong> ${esc(state.companyName)}</p>
			<p><strong>Produto:</strong> ${esc(state.productLabel)}</p>
			<p><strong>Período:</strong> ${esc($('#kdx-df', work).value)} a ${esc($('#kdx-dt', work).value)}</p>${stock}
			<table><thead><tr>${head}</tr></thead><tbody>${body}</tbody></table></body></html>`;
		const w = window.open('', '_blank');
		if (!w) {
			notify('Permita pop-up para gerar o PDF.', 'Kardex');
			return;
		}
		w.document.write(html);
		w.document.close();
		w.onload = () => { w.focus(); w.print(); };
	}

	await loadCompanies($('#kdx-company', work));
	defaultDates();
	syncLegenda();

	$('#kdx-company', work).addEventListener('change', () => {
		$('#kdx-product-id', work).value = '';
		$('#kdx-prod-sel', work).textContent = '';
		state.productId = '';
		state.productLabel = '';
	});

	$('#kdx-btn-buscar-prod', work).addEventListener('click', () => { handleBuscarProd(); });

	$('#kdx-busca-prod', work).addEventListener('keydown', async (e) => {
		if (e.key !== 'Enter') return;
		e.preventDefault();
		const ok = await handleBuscarProd();
		if (ok) await runConsult();
	});

	$('#kdx-vista-cliente', work).addEventListener('change', () => {
		syncLegenda();
		if (state.productId) runConsult();
	});

	$('#kdx-consultar', work).addEventListener('click', runConsult);

	$('#kdx-prev', work).addEventListener('click', async () => {
		state.offset = Math.max(0, state.offset - KDX_PAGE_LIMIT);
		try {
			const payload = await fetchKardexPage(state.offset, KDX_PAGE_LIMIT);
			const raw = payload.rows || [];
			const rows = await processRows(raw, { vistaCliente: false });
			renderTable(rows, false);
			$('#kdx-rows-meta', work).textContent = `Total: ${fmtNumberPt(state.total)} · página a partir de ${state.offset + 1}`;
			$('#kdx-prev', work).disabled = state.offset <= 0;
			$('#kdx-next', work).disabled = raw.length < KDX_PAGE_LIMIT;
		} catch (e) {
			showErr(e?.message || 'Falha na paginação.');
		}
	});

	$('#kdx-next', work).addEventListener('click', async () => {
		state.offset += KDX_PAGE_LIMIT;
		try {
			const payload = await fetchKardexPage(state.offset, KDX_PAGE_LIMIT);
			const raw = payload.rows || [];
			const rows = await processRows(raw, { vistaCliente: false });
			renderTable(rows, false);
			$('#kdx-rows-meta', work).textContent = `Total: ${fmtNumberPt(state.total)} · página a partir de ${state.offset + 1}`;
			$('#kdx-prev', work).disabled = false;
			$('#kdx-next', work).disabled = raw.length < KDX_PAGE_LIMIT;
		} catch (e) {
			showErr(e?.message || 'Falha na paginação.');
		}
	});

	async function runExport(kind) {
		if (!state.productId) {
			showErr('Selecione um produto antes de exportar.');
			return;
		}
		const btn = kind === 'pdf' ? $('#kdx-pdf', work) : $('#kdx-csv', work);
		btn.disabled = true;
		try {
			const rows = await buildExportRows();
			if (kind === 'pdf') downloadPdf(rows);
			else downloadCsv(rows);
		} catch (e) {
			showErr(e?.message || 'Falha na exportação.');
		} finally {
			btn.disabled = false;
		}
	}

	$('#kdx-pdf', work).addEventListener('click', () => runExport('pdf'));
	$('#kdx-csv', work).addEventListener('click', () => runExport('csv'));
}
